import {
  NavContentBlueprint,
  type NavContentNavItem,
} from '@backstage/plugin-app-react';
import { ButtonIcon, Link } from '@backstage/ui';
import { RiCloseLine, RiMenuLine } from '@remixicon/react';
import {
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { verdantTokens } from '../../theme/verdantTokens';
import { LogoFull } from './LogoFull';
import styles from './Nav.module.css';

export type NavigationItem = {
  href: string;
  icon: ReactNode;
  id: string;
  title: string;
};

type NavigationProps = {
  items: NavigationItem[];
};

const isCurrentRoute = (pathname: string, href: string) =>
  pathname === href || (href !== '/' && pathname.startsWith(`${href}/`));

const DesktopNavigationLink = ({ item }: { item: NavigationItem }) => {
  const { pathname } = useLocation();
  const active = isCurrentRoute(pathname, item.href);

  return (
    <Link
      href={item.href}
      standalone
      className={`${styles.desktopNavLink} ${
        active ? styles.desktopNavLinkActive : ''
      }`}
      aria-current={active ? 'page' : undefined}
    >
      <span className={styles.desktopNavIcon} aria-hidden="true">
        {item.icon}
      </span>
      <span>{item.title}</span>
    </Link>
  );
};

const workspaceIds = new Set([
  'page:catalog',
  'page:database-subsetting',
  'page:techdocs',
]);

export const DesktopNavigation = ({ items }: NavigationProps) => {
  const workspace = items.filter(item => workspaceIds.has(item.id));
  const utilities = items.filter(item => !workspaceIds.has(item.id));

  return (
    <aside
      className={styles.desktopNavigation}
      aria-label="Application sidebar"
    >
      <Link
        href="/"
        standalone
        className={styles.desktopHomeLink}
        aria-label="Tigo Developer Portal home"
      >
        <LogoFull />
      </Link>
      <nav className={styles.desktopNav} aria-label="Primary navigation">
        <div>
          <p className={styles.desktopNavSectionLabel}>Workspace</p>
          <div className={styles.desktopNavGroup}>
            {workspace.map(item => (
              <DesktopNavigationLink key={item.id} item={item} />
            ))}
          </div>
        </div>
        {utilities.length > 0 && (
          <div className={styles.desktopNavUtilities}>
            <p className={styles.desktopNavSectionLabel}>Tools &amp; account</p>
            <div className={styles.desktopNavGroup}>
              {utilities.map(item => (
                <DesktopNavigationLink key={item.id} item={item} />
              ))}
            </div>
          </div>
        )}
      </nav>
    </aside>
  );
};

export const MobileTopBar = ({ items }: NavigationProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const location = useLocation();
  const drawerId = useId();
  const titleId = `${drawerId}-title`;
  const closeDrawer = useCallback((restoreFocus: boolean) => {
    setIsOpen(false);
    if (restoreFocus) {
      window.requestAnimationFrame(() => menuButtonRef.current?.focus());
    }
  }, []);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const desktop = window.matchMedia(
      `(min-width: ${verdantTokens.breakpoints.mobileMax + 1}px)`,
    );
    const closeAtDesktop = (event: MediaQueryListEvent | MediaQueryList) => {
      if (event.matches) closeDrawer(false);
    };

    closeAtDesktop(desktop);
    desktop.addEventListener('change', closeAtDesktop);
    return () => desktop.removeEventListener('change', closeAtDesktop);
  }, [closeDrawer]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return undefined;

    const handleCancel = (event: Event) => {
      event.preventDefault();
      closeDrawer(true);
    };
    const handleClose = () => {
      setIsOpen(false);
    };

    dialog.addEventListener('cancel', handleCancel);
    dialog.addEventListener('close', handleClose);
    return () => {
      dialog.removeEventListener('cancel', handleCancel);
      dialog.removeEventListener('close', handleClose);
    };
  }, [closeDrawer]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return undefined;

    if (!isOpen) {
      if (dialog.open) {
        if (typeof dialog.close === 'function') dialog.close();
        else dialog.removeAttribute('open');
      }
      return undefined;
    }

    if (!dialog.open) {
      try {
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
      } catch {
        dialog.setAttribute('open', '');
      }
    }

    const frame = window.requestAnimationFrame(() => {
      dialog
        .querySelector<HTMLAnchorElement>('[data-mobile-navigation-link]')
        ?.focus();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isOpen]);

  const handleDrawerKeyDown = (event: KeyboardEvent<HTMLDialogElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeDrawer(true);
    }
  };

  return (
    <div className={styles.mobileNavigation}>
      <header className={styles.mobileTopBar}>
        <Link
          href="/"
          standalone
          className={styles.mobileHomeLink}
          aria-label="Tigo Developer Portal home"
        >
          <LogoFull />
        </Link>
        <ButtonIcon
          ref={menuButtonRef}
          className={styles.mobileMenuButton}
          variant="tertiary"
          icon={
            isOpen ? (
              <RiCloseLine aria-hidden="true" />
            ) : (
              <RiMenuLine aria-hidden="true" />
            )
          }
          aria-controls={drawerId}
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          aria-label={isOpen ? 'Close main navigation' : 'Open main navigation'}
          onPress={() => {
            if (isOpen) {
              closeDrawer(true);
            } else {
              setIsOpen(true);
            }
          }}
        />
      </header>

      <dialog
        ref={dialogRef}
        id={drawerId}
        className={styles.mobileDrawer}
        aria-labelledby={titleId}
        onKeyDown={handleDrawerKeyDown}
      >
        <div className={styles.mobileDrawerHeader}>
          <h2 id={titleId} className={styles.mobileDrawerTitle}>
            Navigation
          </h2>
          <ButtonIcon
            className={styles.mobileDrawerClose}
            variant="tertiary"
            icon={<RiCloseLine aria-hidden="true" />}
            aria-label="Close main navigation"
            onPress={() => closeDrawer(true)}
          />
        </div>
        <nav className={styles.mobileNavList} aria-label="Primary navigation">
          {items.map(item => {
            const active = isCurrentRoute(location.pathname, item.href);
            return (
              <Link
                key={item.id}
                href={item.href}
                standalone
                className={`${styles.mobileNavLink} ${
                  active ? styles.mobileNavLinkActive : ''
                }`}
                data-mobile-navigation-link
                aria-current={active ? 'page' : undefined}
                onClick={() => closeDrawer(false)}
              >
                <span className={styles.mobileNavIcon} aria-hidden="true">
                  {item.icon}
                </span>
                <span>{item.title}</span>
              </Link>
            );
          })}
        </nav>
      </dialog>
    </div>
  );
};

const preferredOrder = [
  'page:catalog',
  'page:database-subsetting',
  'page:techdocs',
  'page:search',
  'page:notifications',
  'page:user-settings',
] as const;

const navigationOrder = (item: NavContentNavItem) => {
  const preferred = preferredOrder.indexOf(
    item.node.spec.id as (typeof preferredOrder)[number],
  );
  return preferred === -1 ? preferredOrder.length : preferred;
};

export const SidebarContent = NavContentBlueprint.make({
  params: {
    component: ({ navItems }) => {
      const items = navItems
        .clone()
        .rest()
        .sort(
          (left, right) =>
            navigationOrder(left) - navigationOrder(right) ||
            left.title.localeCompare(right.title),
        )
        .map(({ href, icon, node, title }) => ({
          href,
          icon,
          id: node.spec.id,
          title,
        }));

      return (
        <>
          <DesktopNavigation items={items} />
          <MobileTopBar items={items} />
        </>
      );
    },
  },
});
